import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import deviceService from '../services/deviceService';
import authService from '../services/auth';
import deviceRepository from '../services/repositories/deviceRepository';
import {
  aggregateDevices, deriveStatus, DEVICE_STATUS,
  targetValveState, valveStateOf, deriveValveView,
} from '../domain/device';
import { evaluateValveCommandReport } from '../domain/valveCommand';

const DeviceContext = createContext();

export const useDevices = () => {
  const context = useContext(DeviceContext);
  if (!context) {
    throw new Error('useDevices debe ser usado dentro de un DeviceProvider');
  }
  return context;
};

/** Ambito de la vista: toda la cuenta, o un dispositivo concreto. */
export const VIEW_SCOPE = {
  ACCOUNT: 'account',
  DEVICE: 'device',
};

const LS_DEVICE = 'selectedDeviceId';
const LS_SCOPE = 'nissaguard.viewScope';
const REFRESH_MS = 30000;

/* La telemetría del firmware sale cada 30 s (TELEMETRY_INTERVAL en
   esp32-aws-iot.hpp). El ACK del topic de status persiste una confirmación
   inmediata y la telemetría conserva el estado físico como respaldo periódico.
   El sondeo cubre ambos canales y concede dos ciclos y medio antes de marcar la
   orden como no confirmada. */
const VALVE_POLL_MS = 5000;
const VALVE_CONFIRM_TIMEOUT_MS = 75000;

export const DeviceProvider = ({ children }) => {
  const [devices, setDevices] = useState([]);

  // Se guarda el ID, no el objeto. Antes se almacenaba el dispositivo entero, lo
  // que obligaba a un efecto extra para resincronizarlo cada vez que llegaba la
  // lista nueva, y provocaba renders en cascada. El objeto se deriva del ID.
  const [selectedDeviceId, setSelectedDeviceId] = useState(
    () => localStorage.getItem(LS_DEVICE) || null
  );
  const [viewScope, setViewScope] = useState(
    () => localStorage.getItem(LS_SCOPE) || VIEW_SCOPE.ACCOUNT
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [linking, setLinking] = useState(false);

  /* Comando de válvula en curso, siempre atado a SU dispositivo. Guardarlo con
     `deviceId` es lo que impide que el aviso de un equipo se muestre sobre otro. */
  const [valveCommand, setValveCommand] = useState(null);
  const comandoActivo = useRef(0);

  // Evita que un refresco silencioso en vuelo pise un cambio del usuario.
  const peticionActiva = useRef(0);

  const selectedDevice = useMemo(
    () => devices.find((d) => d.device_id === selectedDeviceId) ?? null,
    [devices, selectedDeviceId]
  );

  const summary = useMemo(() => aggregateDevices(devices), [devices]);

  /**
   * Carga la lista de dispositivos.
   *
   * Antes esta funcion pedia la telemetria de CADA dispositivo por separado
   * dentro de un Promise.all, y lo repetia cada 30 segundos: con 10 dispositivos
   * eran 11 llamadas por refresco. Ahora una sola llamada devuelve la lista ya
   * enriquecida, que es como debe comportarse el endpoint real.
   */
  const loadUserDevices = useCallback(async (silencioso = false) => {
    const id = ++peticionActiva.current;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const lista = await deviceRepository.list();
      if (id !== peticionActiva.current) return;   // llegó tarde: se descarta
      setDevices(lista);
    } catch (err) {
      if (id !== peticionActiva.current) return;
      console.error('Error cargando dispositivos:', err);
      setError(err.message || 'No se pudieron cargar los dispositivos');
    } finally {
      if (id === peticionActiva.current && !silencioso) setLoading(false);
    }
  }, []);

  const loadUserProfile = useCallback(async () => {
    // En demostración el perfil sale de la sesión local: llamar a la API real
    // sólo produciría un 401 inútil.
    if (authService.isDemo()) {
      setUserProfile(authService.getUserInfo());
      return;
    }
    try {
      setUserProfile(await deviceService.getUserProfile());
    } catch (err) {
      // El perfil es informativo: su fallo no debe bloquear el panel.
      console.warn('No se pudo cargar el perfil:', err.message);
    }
  }, []);

  /**
   * Reconciliacion de la seleccion tras cada carga.
   *
   * Sustituye al `setTimeout(1000)` que antes esperaba "a ver si ya cargó":
   * si la red tardaba mas de un segundo, la seleccion guardada se perdia. Ahora
   * se resuelve cuando los datos existen de verdad, no cuando vence un temporizador.
   */
  useEffect(() => {
    if (loading || devices.length === 0) return;
    const sigueExistiendo = devices.some((d) => d.device_id === selectedDeviceId);
    if (!sigueExistiendo) {
      const primero = devices[0].device_id;
      setSelectedDeviceId(primero);
      localStorage.setItem(LS_DEVICE, primero);
    }
  }, [devices, loading, selectedDeviceId]);

  const selectDevice = useCallback((device) => {
    const id = typeof device === 'string' ? device : device?.device_id ?? null;
    setSelectedDeviceId(id);
    setViewScope(VIEW_SCOPE.DEVICE);
    if (id) localStorage.setItem(LS_DEVICE, id);
    localStorage.setItem(LS_SCOPE, VIEW_SCOPE.DEVICE);
  }, []);

  const selectAccountScope = useCallback(() => {
    setViewScope(VIEW_SCOPE.ACCOUNT);
    localStorage.setItem(LS_SCOPE, VIEW_SCOPE.ACCOUNT);
  }, []);

  /**
   * Vincula un dispositivo nuevo.
   * SIMULADO: hoy responde el mock. El repositorio ya apunta al endpoint real
   * para cuando exista, asi que esta funcion no cambiara.
   */
  const linkDevice = useCallback(
    async ({ serial, code }) => {
      setLinking(true);
      try {
        const nuevo = await deviceRepository.link({ serial, code });
        await loadUserDevices(true);
        selectDevice(nuevo.device_id);
        return nuevo;
      } finally {
        setLinking(false);
      }
    },
    [loadUserDevices, selectDevice]
  );

  const unlinkDevice = useCallback(
    async (deviceId) => {
      await deviceRepository.unlink(deviceId);
      if (deviceId === selectedDeviceId) {
        setSelectedDeviceId(null);
        selectAccountScope();
      }
      await loadUserDevices(true);
    },
    [loadUserDevices, selectedDeviceId, selectAccountScope]
  );

  const renameDevice = useCallback(
    async (deviceId, cambios) => {
      const actualizado = await deviceRepository.rename(deviceId, cambios);
      await loadUserDevices(true);
      return actualizado;
    },
    [loadUserDevices]
  );

  const refreshData = useCallback(async () => {
    await Promise.all([loadUserDevices(), loadUserProfile()]);
  }, [loadUserDevices, loadUserProfile]);

  /* ---- Operaciones sobre el dispositivo seleccionado ----
     Siguen usando deviceService porque esos endpoints SI existen en el backend
     real. Solo la gestion multi-dispositivo esta simulada. */

  /* Operaciones que el backend real sí expone pero el mock no necesita
     replicar: en demostración se resuelven con el estado simulado o se
     ignoran, en lugar de llamar a la API y recibir un 401. */
  const getSelectedDeviceStats = useCallback(
    async (hours = 24) => {
      if (!selectedDevice) return null;
      if (authService.isDemo()) {
        return {
          device_id: selectedDevice.device_id,
          total_liters: selectedDevice.monthly_consumption ?? 0,
          daily_consumption: selectedDevice.daily_consumption ?? 0,
          hours,
        };
      }
      return deviceService.getDeviceStatistics(selectedDevice.device_id, hours);
    },
    [selectedDevice]
  );

  /**
   * Acciona la válvula del dispositivo SELECCIONADO y espera confirmación.
   *
   * Antes esto hacía una actualización optimista: escribía el estado nuevo en
   * la lista local nada más responder la API y daba la orden por buena. El
   * problema es lo que significa esa respuesta —la Lambda contesta 200 en
   * cuanto publica el comando MQTT, sin esperar al equipo—, así que la
   * interfaz pasaba a "Cerrada" aunque el dispositivo estuviera desconectado y
   * el mensaje se hubiera descartado. Es exactamente el cambio "sólo visual"
   * que no debe existir en una válvula de agua.
   *
   * Ahora se distingue lo que pedimos de lo que el equipo confirma: se sondea
   * el identificador del comando y su estado reportado. Si no llega a tiempo,
   * la válvula se sigue mostrando en su último estado confirmado y quien llama
   * recibe `confirmed: false` para decirlo con claridad.
   */
  const controlSelectedDeviceValve = useCallback(
    async (action) => {
      if (!selectedDevice) throw new Error('No hay dispositivo seleccionado');
      if (action !== 'open' && action !== 'close') {
        throw new Error('Selecciona explícitamente abrir o cerrar la válvula');
      }
      const deviceId = selectedDevice.device_id;
      const objetivo = targetValveState(action, valveStateOf(selectedDevice));
      const sentAtSeconds = Math.floor(Date.now() / 1000);

      const token = ++comandoActivo.current;
      const vigente = () => token === comandoActivo.current;

      setValveCommand({ deviceId, action, target: objetivo, phase: 'sending', waitedMs: 0 });

      let resultado;
      try {
        resultado = await deviceRepository.controlValve(deviceId, action);
      } catch (err) {
        if (vigente()) setValveCommand({ deviceId, action, target: objetivo, phase: 'failed', error: err.message });
        throw err;
      }

      // En demostración el mock ES la fuente de verdad y ya aplicó el cambio:
      // no hay dispositivo al que esperar, así que confirmar es inmediato.
      if (authService.isDemo()) {
        await loadUserDevices(true);
        if (vigente()) setValveCommand({ deviceId, action, target: objetivo, phase: 'confirmed', simulated: true });
        return { ...resultado, confirmed: true, state: objetivo, simulated: true };
      }

      // Cuenta real: la confirmación sólo puede venir del propio equipo, en su
      // siguiente telemetría. Ese es el motivo del margen de espera.
      if (vigente()) setValveCommand({ deviceId, action, target: objetivo, phase: 'awaiting', waitedMs: 0 });

      const inicio = Date.now();
      let ultimoEstado = valveStateOf(selectedDevice);
      while (Date.now() - inicio < VALVE_CONFIRM_TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, VALVE_POLL_MS));
        if (!vigente()) return { ...resultado, confirmed: false, cancelled: true };

        try {
          const lectura = await deviceRepository.readValveState(deviceId);
          if (!vigente()) return { ...resultado, confirmed: false, cancelled: true };
          setDevices((prev) => prev.map((d) => (d.device_id === deviceId ? lectura.device : d)));
          ultimoEstado = lectura.valve_state;

          const confirmacion = evaluateValveCommandReport({
            device: lectura.device,
            command: resultado,
            target: objetivo,
            initialDevice: selectedDevice,
            sentAtSeconds,
          });
          if (confirmacion.status === 'rejected') {
            if (vigente()) setValveCommand({
              deviceId, action, target: objetivo, phase: 'rejected',
              commandStatus: confirmacion.commandStatus,
            });
            return { ...resultado, confirmed: false, rejected: true, state: ultimoEstado };
          }
          if (confirmacion.status === 'confirmed') {
            if (vigente()) setValveCommand({
              deviceId, action, target: objetivo, phase: 'confirmed',
              confirmationMode: confirmacion.confirmationMode,
            });
            return { ...resultado, confirmed: true, state: objetivo, confirmationMode: confirmacion.confirmationMode };
          }
        } catch (err) {
          // Un fallo de red puntual no invalida el comando: puede haber salido.
          console.warn('Sondeo de válvula fallido, se reintenta:', err.message);
        }
        if (vigente()) {
          setValveCommand((c) => (c && c.deviceId === deviceId ? { ...c, waitedMs: Date.now() - inicio } : c));
        }
      }

      if (vigente()) setValveCommand({ deviceId, action, target: objetivo, phase: 'unconfirmed' });
      return { ...resultado, confirmed: false, state: ultimoEstado };
    },
    [selectedDevice, loadUserDevices]
  );

  /** Descarta el aviso del último comando (al cambiar de equipo o al cerrarlo). */
  const clearValveCommand = useCallback(() => {
    comandoActivo.current += 1;   // invalida cualquier sondeo en vuelo
    setValveCommand(null);
  }, []);

  /* El aviso pertenece al dispositivo sobre el que se lanzó. Si el usuario
     cambia de equipo, no debe seguir viendo "esperando confirmación" sobre una
     válvula que no es la que está mirando. */
  useEffect(() => {
    if (valveCommand && valveCommand.deviceId !== selectedDeviceId) clearValveCommand();
  }, [selectedDeviceId, valveCommand, clearValveCommand]);

  const updateSelectedDeviceConfig = useCallback(
    async (config) => {
      if (!selectedDevice) throw new Error('No hay dispositivo seleccionado');
      const r = await deviceRepository.updateConfig(selectedDevice.device_id, config);
      await loadUserDevices(true);
      return r;
    },
    [selectedDevice, loadUserDevices]
  );

  const getSelectedDeviceHealth = useCallback(async () => {
    if (!selectedDevice) return null;
    if (authService.isDemo()) {
      return {
        device_id: selectedDevice.device_id,
        status: deriveStatus(selectedDevice),
        wifi_rssi: selectedDevice.wifi_rssi,
        free_heap: 178160,
        firmware_version: selectedDevice.firmware_version,
      };
    }
    return deviceService.getDeviceHealth(selectedDevice.device_id);
  }, [selectedDevice]);

  const restartSelectedDevice = useCallback(async () => {
    if (!selectedDevice) throw new Error('No hay dispositivo seleccionado');
    // Reiniciar un equipo real desde una cuenta de prueba no debe ser posible.
    if (authService.isDemo()) return { status: 'executed', simulated: true };
    return deviceService.restartDevice(selectedDevice.device_id);
  }, [selectedDevice]);

  const hasDevicePermission = useCallback(
    (deviceId) => devices.some((d) => d.device_id === deviceId),
    [devices]
  );

  /**
   * Carga inicial y refresco periodico, atados a la SESION y no al montaje.
   *
   * Aqui estaba el fallo por el que los dispositivos no aparecian hasta pulsar
   * "Actualizar" a mano en el perfil. Este proveedor se monta una sola vez, en
   * `index.js`, por encima del router y por tanto ANTES del login. El efecto
   * comprobaba `isAuthenticated()` en ese momento -cuando todavia no hay nadie
   * dentro-, se rendia con un `return` y ni siquiera dejaba armado el
   * intervalo. Como el login navega con el router y no recarga la pagina, el
   * proveedor no se vuelve a montar y ese efecto no se ejecutaba nunca mas: la
   * lista se quedaba vacia hasta que algo llamaba a `refreshData()` por su
   * cuenta. Recargar la pagina despues de entrar lo arreglaba, y por eso
   * parecia intermitente.
   *
   * La solucion no es reintentar por si acaso, sino escuchar: `authService`
   * avisa al entrar, al entrar en demostracion y al salir, y la carga se
   * dispara con ese aviso.
   */
  useEffect(() => {
    let intervalo = null;

    const detener = () => {
      if (intervalo) {
        clearInterval(intervalo);
        intervalo = null;
      }
    };

    const sincronizar = (estaAutenticado) => {
      detener();

      if (!estaAutenticado) {
        // Al cerrar sesion no basta con dejar de pedir datos: hay que soltar
        // los que ya estaban en memoria. Si no, el siguiente en entrar veria
        // por un instante los dispositivos del anterior.
        setDevices([]);
        setUserProfile(null);
        setLoading(false);
        return;
      }

      loadUserDevices();
      loadUserProfile();
      intervalo = setInterval(() => loadUserDevices(true), REFRESH_MS);
    };

    // Estado actual (cubre el caso de recargar la pagina ya con sesion)...
    sincronizar(authService.isAuthenticated());
    // ...y cada cambio posterior.
    const desuscribir = authService.onSessionChange(sincronizar);

    return () => {
      desuscribir();
      detener();
    };
  }, [loadUserDevices, loadUserProfile]);

  const value = useMemo(
    () => ({
      // Estado
      devices,
      selectedDevice,
      selectedDeviceId,
      loading,
      error,
      userProfile,
      linking,

      // Ambito de vista
      viewScope,
      isAccountScope: viewScope === VIEW_SCOPE.ACCOUNT,
      selectAccountScope,

      // Agregado de la cuenta
      summary,

      // Acciones
      loadUserDevices,
      loadUserProfile,
      selectDevice,
      refreshData,
      linkDevice,
      unlinkDevice,
      renameDevice,

      // Dispositivo seleccionado
      getSelectedDeviceStats,
      controlSelectedDeviceValve,
      valveCommand,
      clearValveCommand,
      valveOf: (device) => deriveValveView(device),
      updateSelectedDeviceConfig,
      getSelectedDeviceHealth,
      restartSelectedDevice,

      // Utilidades
      hasDevicePermission,
      canControlDevices: () => deviceService.isOperator(),
      isAdmin: () => deviceService.isAdmin(),
      statusOf: (device) => deriveStatus(device),
      DEVICE_STATUS,
      userRole: deviceService.getUserRole(),
    }),
    [
      devices, selectedDevice, selectedDeviceId, loading, error, userProfile, linking,
      viewScope, summary, selectAccountScope, loadUserDevices, loadUserProfile,
      selectDevice, refreshData, linkDevice, unlinkDevice, renameDevice,
      getSelectedDeviceStats, controlSelectedDeviceValve, valveCommand, clearValveCommand,
      updateSelectedDeviceConfig,
      getSelectedDeviceHealth, restartSelectedDevice, hasDevicePermission,
    ]
  );

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};
